import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Tenant } from '@core/model';
import { HomeService } from '@core/service/home.service';
import { ProviderAccountDTO } from '@mynexus/mynexus-ui-lib';
import { ConfirmationService } from 'primeng/api';
import { Observable } from 'rxjs';
import { filter, finalize, switchMap, tap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

const NPI_LENGTH = 10;

@Component({
    selector: 'app-account',
    templateUrl: './account.component.html',
    styleUrls: ['./account.component.scss'],
    providers: [ConfirmationService],
    standalone: false
})
export class AccountComponent implements OnInit {
  private destroyRef = inject(DestroyRef);

  @Input() account: Tenant = null;

  @Input() set providers(value: { [key: string]: ProviderAccountDTO }) {
    this.addedProviders = Object.values(value).map(p => ({
      label: this.createNPILabel(p),
      value: p
    }));
  }

  @Input() isHHProviderType: boolean;
  @Input() set selectedBusinessTypeName(value: string) {
    this.selectedBusinessType = value;

    if (value) {
      this.providerSearchForm.get('npi').enable();
    } else {
      this.providerSearchForm.get('npi').disable();
    }
  }

  get selectedBusinessTypeName(): string {
    return this.selectedBusinessType;
  }

  @Output() add: EventEmitter<ProviderAccountDTO[]> = new EventEmitter<ProviderAccountDTO[]>();
  @Output() remove: EventEmitter<ProviderAccountDTO[]> = new EventEmitter<ProviderAccountDTO[]>();
  @Output() deleteAccount: EventEmitter<Tenant> = new EventEmitter<Tenant>();

  providerSearchForm = new FormGroup({
    npi: new FormControl('', [
      Validators.required,
      Validators.minLength(10),
      Validators.maxLength(10)
    ])
  });

  selectedBusinessType: string;
  facilityAccountsBusinessTypes = ['Post-Acute Facility'];
  isLoading = false;
  selectedProviders: any = [];
  selectedAddedProviders: any[] = [];
  addedProviders: any = [];
  searchResults: any;
  isProviderNotFound = false;

  constructor(private homeService: HomeService, private confirmationService: ConfirmationService) {}

  ngOnInit(): void {
    this.providerSearchForm
      .get('npi')
      .valueChanges.pipe(
        tap(npi => {
          this.selectedProviders = [];

          if (npi?.length < NPI_LENGTH) {
            this.searchResults = [];
            this.isProviderNotFound = false;
          }
        }),
        filter(npi => npi?.length === NPI_LENGTH),
        switchMap(this.searchProvider.bind(this)),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.isLoading = false))
      )
      .subscribe(payload => {
        if (!payload) {
          this.searchResults = [];
          this.isProviderNotFound = true;
          return;
        }

        this.isProviderNotFound = false;
        this.searchResults = payload.map(value => ({
          label: this.createNPILabel(value),
          value
        }));
      });
  }

  addAccount() {
    this.add.emit(this.selectedProviders);
  }

  preventEvent(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  }

  discardAccount() {
    if (this.addedProviders.length > 0) {
      this.confirmationService.confirm({
        message: `You'll lose all added providers to this account. Are you sure you want to process?`,
        accept: () => {
          this.deleteAccount.emit(this.account);
        }
      });
      return;
    }

    this.deleteAccount.emit(this.account);
  }

  removeSelectedProviders() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete selected records?',
      accept: () => {
        this.remove.emit(this.selectedAddedProviders);
        this.selectedAddedProviders = []; // clear selection
      }
    });
  }

  private searchProvider(npi: string) {
    this.isLoading = true;

    let searchMethod: Observable<ProviderAccountDTO[]>;
    if (this.facilityAccountsBusinessTypes.includes(this.selectedBusinessTypeName))
      searchMethod = this.homeService.searchFacilityAccountsByNpi(npi, this.account.tenantId)
    else if (this.selectedBusinessTypeName === 'DMEPOS Provider')
      searchMethod = this.homeService.searchDmeProviderAccountsByNpi(npi, this.account.tenantId)
    else
      searchMethod = this.homeService.searchProviderByNpi(npi, this.account.tenantId)

    return searchMethod.pipe(finalize(() => (this.isLoading = false)));
  }

  private createNPILabel(providerAccount: ProviderAccountDTO) {
    const {
      physicalStreet,
      physicalCity,
      physicalState,
      physicalZip,
      providerName
    } = providerAccount;
    return `${providerName}, ${physicalStreet}, ${physicalCity}, ${physicalState}, ${physicalZip}`;
  }
}
